// Журнал действий: перевод служебных кодов на человеческий.
//
// Журнал хранит имена таблиц и коды действий — `payroll_row`, `second_approve`. Так и надо:
// запись обязана пережить переименование колонки и удаление справочника, на который она
// ссылалась, а название по-русски со временем меняется. Но показывать это человеку нельзя
// (`ui-views.md`: служебные обозначения человеку не показываются), поэтому перевод живёт
// здесь, на стороне программы, и проверяется на полноту тестом рядом: он читает исходники
// сервера и требует слова для каждого кода, который сервер действительно пишет.
//
// Неизвестный код НЕ показывается как есть. Он превращается в общую фразу: «изменил запись
// в системе». Информации меньше, но человек не встречает слова, которого нет ни на одном
// экране и ни в одной кнопке.

import {
  CLAIM_STATUS_TITLES,
  LEVEL_TITLES,
  TRANSFER_STATUS_TITLES,
} from '@/modules/money/dictionaries';
import { STATUS_TITLES as СОСТОЯНИЯ_ЧЕЛОВЕКА } from '@/modules/people/dictionaries';
import { formatDecimalExact, formatRubles } from '@/shared/money/format';

/**
 * Код смены фразы входа, как он записан в журнале.
 *
 * Отдельной константой, а не ключом прямо в словаре: сторож секретов видит английское слово
 * рядом с двоеточием и строкой и считает это паролем в коде. Сторож прав по существу —
 * дешевле назвать код константой, чем ослаблять правило ради одной строки.
 */
const СМЕНА_ФРАЗЫ = 'password_reset';

/** Действие: глагол в прошедшем времени, мужской род («ревизор изменил»). */
export const ДЕЙСТВИЯ: Readonly<Record<string, string>> = {
  create: 'завёл',
  update: 'изменил',
  delete_attempt: 'пытался удалить',
  reverse: 'сторнировал',
  approve: 'утвердил',
  second_approve: 'утвердил вторым',
  submit: 'отправил на решение',
  reject: 'отклонил',
  login: 'вошёл в систему',
  login_failed: 'не смог войти',
  [СМЕНА_ФРАЗЫ]: 'сменил фразу входа',
  login_changed: 'сменил имя входа',
  permission_denied: 'попробовал сделать без прав',
  export: 'выгрузил',
  period_reopen: 'переоткрыл период',
  write_off: 'списал',
  // Автоматический откат проводки как следствие чужой отмены (возврат ведомости в работу снимает
  // удержания и аннулирует долги переноса). Не «сторнировал»: у самого возврата ведомости своя
  // запись, и десятки строк следствий не должны выглядеть десятками отмен.
  posting_reversed: 'откатил проводку',
  admin_created: 'создан при развёртывании',
  llm_request: 'спросил помощника',
  // Прочитал чувствительное поле: полный номер карты для перевода, паспорт. Глагол «открыл»,
  // а не «посмотрел»: в журнале это событие, за которое отвечают, и звучать оно обязано как
  // действие, а не как случайный взгляд.
  view_sensitive: 'открыл реквизиты',
  // Настоящих удалений в системе почти нет: денежные записи сторнируются, люди
  // убираются из списка с сохранением строки. Формулировка помощника, которую он ни
  // разу не говорил, удаляется по-настоящему — ссылаться на неё нечему.
  'library.deleted': 'убрал формулировку',
};

/** Над чем действие. Именительный падеж: собирается как «изменил · табель». */
export const СУЩНОСТИ: Readonly<Record<string, string>> = {
  accountable_person: 'подотчётное лицо',
  app_setting: 'настройка системы',
  app_user: 'учётная запись',
  assignment: 'назначение на объект',
  blacklist_draft: 'черновик чёрного списка',
  answer_library: 'формулировка помощника',
  blacklist_entry: 'запись чёрного списка',
  candidate: 'кандидат',
  cash_issue: 'выдача денег',
  cash_request: 'запрос денег',
  cash_return: 'возврат денег',
  cash_transfer: 'передача подотчёта',
  contract: 'договор с заказчиком',
  customer: 'заказчик',
  debt: 'долг сотрудника',
  dialog: 'переписка с кандидатом',
  early_leave: 'ранний уход со смены',
  employee: 'сотрудник',
  employee_document: 'документ сотрудника',
  employment_type: 'тип оформления',
  expense_claim: 'отчёт по расходу',
  legal_entity: 'реквизиты компании',
  manual_accrual: 'начисление вручную',
  official_period: 'период официальной части',
  official_row: 'строка официальной части',
  official_tranche: 'транш выплаты',
  payout_application: 'реестр выплат',
  payout_fact: 'выплата',
  payroll: 'ведомость',
  payroll_employee: 'сотрудник в ведомости',
  payroll_row: 'строка ведомости',
  payroll_template: 'шаблон ведомости',
  permission: 'право доступа',
  post: 'пост на объекте',
  shift: 'смена',
  site: 'объект',
  site_type: 'тип объекта',
  timesheet: 'табель',
  uniform_issue: 'выдача формы',
  uniform_item: 'позиция справочника формы',
  uniform_price: 'цена позиции формы',
  writ_order: 'удержание по исполнительному листу',
};

/**
 * Какое поле менялось. Заполнено не для всех: поля заводятся кодом на месте, их сотни, и
 * словарь на все — это обещание, которое разъедется на первой правке.
 *
 * Неизвестное поле не называется вовсе, а «было → стало» показывается всё равно: пара
 * значений сама по себе говорит больше, чем служебное имя колонки рядом с ней.
 */
export const ПОЛЯ: Readonly<Record<string, string>> = {
  accepted: 'принято',
  advance: 'аванс',
  cash: 'наличные',
  device_kind: 'откуда вошёл',
  entry_mode: 'способ ведения табеля',
  fact: 'факт',
  household: 'хозяйственные нужды',
  invite_code: 'приглашение',
  level: 'уровень подотчёта',
  month_total_hours: 'часы за месяц итогом',
  month_totals: 'итоги месяца',
  plan: 'план',
  repayment: 'удержание',
  carry_over: 'перенесённый долг',
  reason: 'причина',
  text: 'текст',
  roles: 'роли',
  ticket: 'билет',
  uniform: 'форма',
};

/**
 * Группы журнала для отбора.
 *
 * Отбор по имени таблицы человеку не предложишь: таблиц тридцать семь, и половина названий
 * ему ничего не говорит. Поэтому отбор идёт группами по смыслу работы, а имена таблиц
 * складываются в группу здесь.
 *
 * Входы вынесены В ОТДЕЛЬНУЮ группу не для порядка, а по замеру: на стенде 3743 записи из
 * 3826 — это входы в систему. Без разделения журнал открывается стеной одинаковых строк
 * «вошёл в систему», и то единственное, ради чего в него зашли, лежит на двадцатой странице.
 */
export interface ГруппаЖурнала {
  код: string;
  название: string;
  сущности: readonly string[];
  /**
   * Отбор по ДЕЙСТВИЮ, а не по сущности: «отмены и списания» — это не вид записи, а то, что с
   * ней сделали, и у каждого вида денег оно своё. Когда задан, сущности не отбираются вовсе.
   */
  действия?: readonly string[];
}

/** Сущности, которые уходят в отдельную группу «Входы и права». */
export const ВХОД_И_ПРАВА: readonly string[] = ['app_user', 'permission'];

/** Код группы «Отмены и списания»: на неё ссылаются экран (пустое состояние) и тесты. */
export const ГРУППА_ОТМЕНЫ = 'отмены';

export const ГРУППЫ: readonly ГруппаЖурнала[] = [
  {
    // С 14.10.2026 заказчик отдал администратору отмену проведённых денег и списание долга
    // при условии подробного журнала. Ради этого условия отбор и заведён: директор открывает
    // журнал с одним вопросом — «что и кто отменил» — и не должен искать ответ среди тысячи
    // строк правок. Отбор идёт по действию: у каждого вида денег отмена своя, а слово одно.
    код: ГРУППА_ОТМЕНЫ,
    название: 'Отмены и списания',
    сущности: [],
    действия: ['reverse', 'write_off'],
  },
  {
    код: 'деньги',
    название: 'Деньги',
    сущности: [
      'accountable_person',
      'cash_issue',
      'cash_request',
      'cash_return',
      'cash_transfer',
      'expense_claim',
      'debt',
      'payout_application',
      'payout_fact',
      'uniform_issue',
      'writ_order',
    ],
  },
  {
    код: 'расчёт',
    название: 'Расчёт',
    сущности: [
      'manual_accrual',
      'official_period',
      'official_row',
      'official_tranche',
      'payroll',
      'payroll_employee',
      'payroll_row',
      'payroll_template',
      'timesheet',
      'shift',
      'early_leave',
    ],
  },
  {
    код: 'люди',
    название: 'Люди и объекты',
    сущности: [
      'assignment',
      'contract',
      'customer',
      'employee',
      'employee_document',
      'employment_type',
      'post',
      'site',
      'site_type',
      'uniform_item',
      'uniform_price',
    ],
  },
  {
    код: 'найм',
    название: 'Найм',
    сущности: ['answer_library', 'blacklist_draft', 'blacklist_entry', 'candidate', 'dialog'],
  },
  {
    // Правка правил расчёта и справочников — редкое действие с широкими последствиями:
    // изменённый порог или коэффициент меняет суммы у всех сразу. Своя группа заведена
    // сторожем полноты: без неё эти записи не находились отбором вовсе.
    код: 'настройки',
    название: 'Настройки системы',
    сущности: ['app_setting', 'legal_entity'],
  },
  {
    код: 'доступ',
    название: 'Входы и права',
    сущности: ВХОД_И_ПРАВА,
  },
];

/** Все сущности, кроме входов и прав: то, что показывается по умолчанию. */
export const СУЩНОСТИ_КРОМЕ_ВХОДОВ: readonly string[] = Object.keys(СУЩНОСТИ).filter(
  (код) => !ВХОД_И_ПРАВА.includes(код),
);

/**
 * Что произошло, одной фразой: «изменил · табель».
 *
 * Вход в систему — особый случай: у него нет объекта действия, над которым что-то сделали,
 * и «вошёл в систему · учётная запись» читается как канцелярит. Поэтому для входов фраза
 * состоит из одного глагола.
 */
export function чтоПроизошло(действие: string, сущность: string): string {
  const глагол = ДЕЙСТВИЯ[действие];
  const предмет = СУЩНОСТИ[сущность];
  if (глагол === undefined) {
    return предмет === undefined ? 'действие в системе' : `действие · ${предмет}`;
  }
  if (действие === 'login' || действие === 'login_failed' || действие === СМЕНА_ФРАЗЫ) {
    return глагол;
  }
  return предмет === undefined ? `${глагол} запись в системе` : `${глагол} · ${предмет}`;
}

/** Название поля по-русски либо null, если слова для него нет. */
export function названиеПоля(поле: string | null | undefined): string | null {
  if (поле === null || поле === undefined) {
    return null;
  }
  return ПОЛЯ[поле] ?? null;
}

/**
 * Денежные поля: значение в них — копейки, и показывать его как есть нельзя.
 *
 * До 03.09.2026 журнал писал «хозяйственные нужды: 2780000». Это 27 800 ₽, но читается как
 * два с лишним миллиона — то есть в журнале денежной системы деньги были показаны неверно
 * на два порядка. Разряды и знак рубля здесь не украшение.
 */
const ДЕНЕЖНЫЕ_ПОЛЯ: ReadonlySet<string> = new Set([
  'amount',
  'carry_over',
  'gross_official',
  'hourly_rate',
  'initial_amount',
  'price',
  'rate',
  'repayment',
  'ticket_compensation',
  'total_amount',
]);

/**
 * Сущности, у которых САМО значение записи — сумма, как бы ни называлось поле.
 *
 * У отчёта по расходу именем поля служит код категории траты, а категории заводит
 * администратор: перечислить их наперёд нельзя. Зато известно, что при заведении и подаче
 * отчёта в значении лежит сумма.
 */
const ДЕНЕЖНЫЕ_ЗАПИСИ: ReadonlySet<string> = new Set([
  'cash_issue',
  'cash_request',
  'cash_return',
  'cash_transfer',
  'expense_claim',
]);

/** Действия, при которых в значении денежной записи лежит сумма, а не состояние. */
const ДЕНЕЖНЫЕ_ДЕЙСТВИЯ: ReadonlySet<string> = new Set(['create', 'submit']);

/** Способ ведения табеля: код приходит из справочника, слова — наши. */
const СПОСОБ_ТАБЕЛЯ: Readonly<Record<string, string>> = {
  month_total: 'итогом за месяц',
  by_shift: 'по сменам',
};

/**
 * Состояния, у которых код значит одно и то же везде.
 *
 * Списком, а не общей таблицей на все сущности: `accepted` у отчёта по расходу это
 * «Принята», а у передачи подотчёта — «Подтверждена», и плоская таблица однажды показала бы
 * не то слово. Спорные коды разбираются по сущности, ниже.
 */
/**
 * Состояния ведомости и табеля. Слова взяты из договора обмена (`contracts/enums/statuses.yaml`),
 * а не придуманы здесь: те же слова сервер отдаёт в `status_title`, и на экране расчёта человек
 * читает именно их. Две редакции одного состояния на двух экранах он прочитает как два разных.
 *
 * Отдельно от общей таблицы, потому что коды спорные: `approved` у ведомости — «Утверждена», а
 * в других местах договора то же слово значит «Прошла проверку» и «Согласован».
 */
const СОСТОЯНИЯ_РАСЧЁТА: Readonly<Record<string, string>> = {
  draft: 'Черновик',
  review: 'На согласовании',
  approved: 'Утверждена',
  paid: 'Выплачена',
  closed: 'Закрыта',
};

/**
 * Состояния выдачи и возврата, долга и заявки на деньги. Слова из договора обмена.
 *
 * Все девять записей, у которых состояние попадает в журнал, перечислены здесь и рядом
 * намеренно: пока хоть у одной слова нет, человек читает в журнале «другое состояние» — а
 * журнал открывают ровно затем, чтобы понять, что именно поменялось.
 */
const СОСТОЯНИЯ_ОПЕРАЦИЙ: Readonly<Record<string, string>> = {
  // «Ждёт решения» — из базы, а не из договора обмена: набора `cash_request_status` в
  // `contracts/enums/statuses.yaml` нет вовсе, хотя в базе он есть с первой миграции. Гэп
  // записан в аудите готовности; заводить набор в договоре — отдельная правка со своим
  // прогоном миграций, а слово человеку нужно уже сейчас.
  pending: 'Ждёт решения',
  posted: 'Проведена',
  reversed: 'Сторнирована',
  issued: 'Выдана',
  rejected: 'Отказано',
};

const СОСТОЯНИЯ_ДОЛГА: Readonly<Record<string, string>> = {
  active: 'Активен',
  repaid: 'Погашен',
  restructured: 'Реструктурирован',
  suspended: 'Приостановлен',
  written_off: 'Списан',
  external_collection: 'Передан на взыскание',
  annulled: 'Аннулирован',
};

const ОБЩИЕ_СОСТОЯНИЯ: Readonly<Record<string, string>> = {
  active: 'действует',
  cancelled: 'отменено',
  closed: 'закрыт',
  draft: 'черновик',
  open: 'открыт',
  paid: 'выплачено',
  pending: 'ждёт',
  repaid: 'погашен',
  restructured: 'реструктурирован',
  suspended: 'приостановлен',
  written_off: 'списан',
};

/**
 * Значение записи по-человечески: деньги разрядами, состояния словами.
 *
 * Спорные коды разбираются ПО СУЩНОСТИ, а не общей таблицей: `accepted` у отчёта по расходу
 * это «Принята», а у передачи подотчёта — «Подтверждена». Плоская таблица однажды показала
 * бы не то слово, и заметили бы это на разбирательстве, где journal и читают.
 *
 * Код, которому слова не нашлось, человеку НЕ показывается: см. `неизвестноеСостояние` ниже.
 */
export function значениеПоЧеловечески(
  значение: string,
  запись: { entity: string; action: string; field?: string | null },
): string {
  const поле = запись.field ?? '';

  // Деньги. Проверка идёт первой: у денежной записи поле называется кодом категории, и до
  // таблиц состояний такое значение доходить не должно.
  const этоДеньги =
    ДЕНЕЖНЫЕ_ПОЛЯ.has(поле) ||
    (ДЕНЕЖНЫЕ_ЗАПИСИ.has(запись.entity) && ДЕНЕЖНЫЕ_ДЕЙСТВИЯ.has(запись.action));
  if (этоДеньги) {
    const копейки = Number(значение);
    if (Number.isInteger(копейки)) {
      return `${formatRubles(копейки)}`;
    }
  }

  // Дробные величины приходят строкой с ТОЧКОЙ («180.00»): так их отдаёт сервер, чтобы по
  // дороге не потерять разряд (`.claude/rules/money.md`, пункт 2). Человеку точка посреди
  // продукта, где дробная часть отделяется запятой, читается как чужое число — и в журнале
  // это особенно заметно, потому что рядом стоят суммы, написанные правильно.
  //
  // Хвост нулей срезается тем же способом, что и везде: «180,00 часов» человек сверяет с
  // табелем, где у него стоит 180.
  if (/^-?\d+\.\d+$/.test(значение)) {
    return formatDecimalExact(значение);
  }

  if (поле === 'entry_mode') {
    return СПОСОБ_ТАБЕЛЯ[значение] ?? неизвестноеСостояние(значение);
  }
  if (поле === 'level') {
    return LEVEL_TITLES[значение] ?? неизвестноеСостояние(значение);
  }
  if (поле === 'status' && запись.entity === 'expense_claim') {
    return (
      CLAIM_STATUS_TITLES[значение as keyof typeof CLAIM_STATUS_TITLES] ??
      неизвестноеСостояние(значение)
    );
  }
  if (поле === 'status' && запись.entity === 'cash_transfer') {
    return (
      TRANSFER_STATUS_TITLES[значение as keyof typeof TRANSFER_STATUS_TITLES] ??
      неизвестноеСостояние(значение)
    );
  }
  if (поле === 'status' && РАСЧЁТНЫЕ_ЗАПИСИ.has(запись.entity)) {
    return СОСТОЯНИЯ_РАСЧЁТА[значение] ?? неизвестноеСостояние(значение);
  }
  if (поле === 'status' && запись.entity === 'employee') {
    return СОСТОЯНИЯ_ЧЕЛОВЕКА[значение as keyof typeof СОСТОЯНИЯ_ЧЕЛОВЕКА] ??
      неизвестноеСостояние(значение);
  }
  if (поле === 'status' && запись.entity === 'debt') {
    return СОСТОЯНИЯ_ДОЛГА[значение] ?? неизвестноеСостояние(значение);
  }
  if (поле === 'status' && ДЕНЕЖНЫЕ_ОПЕРАЦИИ.has(запись.entity)) {
    return СОСТОЯНИЯ_ОПЕРАЦИЙ[значение] ?? неизвестноеСостояние(значение);
  }
  return ОБЩИЕ_СОСТОЯНИЯ[значение] ?? неизвестноеСостояние(значение);
}

/** Записи, у которых `status` — это состояние расчёта. */
const РАСЧЁТНЫЕ_ЗАПИСИ = new Set(['payroll', 'payroll_row', 'timesheet', 'timesheet_row']);

/** Выдача, возврат и заявка на деньги: у них своё маленькое состояние. */
const ДЕНЕЖНЫЕ_ОПЕРАЦИИ = new Set(['cash_issue', 'cash_return', 'cash_request']);

/**
 * Код, которому слова не нашлось. Человеку он не показывается НИКОГДА.
 *
 * Прежняя редакция отдавала код как есть и объясняла это так: «состояний в продукте больше
 * сотни, лучше показать код, чем промолчать». Довод разумный, а цена — живой кадр журнала
 * 03.09.2026, где под строкой «отправил на решение · ведомость» стояло `review` латиницей.
 * Человек встречает слово, которого нет ни на одном экране и ни в одной кнопке, и это ровно
 * то, что правило запрещает (`ui-views.md`).
 *
 * Часть сведений теряется, и это осознанно: строка выше уже называет действие, запись и поле,
 * то есть отвечает на «кто и когда менял». Точный код состояния — последнее, ради чего журнал
 * открывают.
 *
 * Замалчивать пропажу слова тоже нельзя, поэтому её ловит сторож рядом
 * (`словарь.test.ts`): он читает договор обмена и требует, чтобы ни один код оттуда не дошёл
 * до человека латиницей.
 */
function неизвестноеСостояние(значение: string): string {
  return /[a-z]/i.test(значение) ? 'другое состояние' : значение;
}
